import {
  createContext,
  useContext,
  type ReactNode,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { authReducer, initialAuthState } from '../reducers/auth-reducer';
import { ApiResponse, AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import { useLogin } from '../api/login.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import authLocalStorage, { TOKEN_KEY, USER_KEY } from '../utils/auth-localstorage';
import { API_URL } from '@/configs/api.config';
import { IUser } from '@/features/user/types/user.type';
import { useUserActions } from '@/features/user/stores/user.store';
import { deleteLocalStorageData } from '@/utils/local-storage';
// import { useNewCaseActions, newCaseStore, defaultFilterCase } from '@/features/case/_shared/stores/_new/new-case.store';
import { useStore } from '@/stores/app-store';
import { analyticStore } from '@/features/analytic/stores/analytic.store';
import { layoutStore } from '@/stores/layout.store';
import { filePreviewStore } from '@/stores/file.store';
import { caseNotificationStore } from '@/features/cases/case/stores/case-notification.store';
import { casesStore, defaultFilterCase } from '@/features/cases/case/stores/cases.store';

// Constants
const USER_ME_ENDPOINT = `${API_URL}/user/me`;
const DEFAULT_REDIRECT_PATH = '/';

// Role types for better type safety
type UserRole = 'admin' | 'agent' | 'supervisor' | 'ticketing-admin' | 'ticketing-member' | 'external-user';

interface AuthContextType {
  isLoadingLogin: boolean;
  user: IUser | null;
  isExternalUser: boolean;
  isExternalUserRole: boolean;
  isAdminRole: boolean;
  isAgentRole: boolean;
  isSupervisorRole: boolean;
  isTicketingAdminRole: boolean;
  isTicketingMemberRole: boolean;
  isNolimitCareRole: boolean;
  isTicketingRole: boolean;
  state: typeof initialAuthState;
  handleLogin: (email: string, password: string) => Promise<void>;
  handleLoginWithToken: (token: string) => Promise<IUser>;
  handleLogout: () => void;
  handleUpdateUser: (updatedUser: IUser) => void;
  isAuthenticated: boolean;
  hydrated: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  // HOOKS
  // const { setListActiveCase, setListUnassignCase, setListResolvedCase } = useNewCaseActions();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const [hydrated, setHydrated] = useState(false);
  const { setUser } = useUserActions();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  // Optimized state derivation - avoid redundant localStorage reads
  const token = state.token;
  const user = state.user;
  const isAuthenticated = Boolean(user);

  // Memoized role calculations to prevent unnecessary re-computations
  const roleChecks = useMemo(() => {
    const roleName = user?.role?.name as UserRole;
    return {
      isAdminRole: roleName === 'admin',
      isAgentRole: roleName === 'agent',
      isSupervisorRole: roleName === 'supervisor',
      isTicketingAdminRole: roleName === 'ticketing-admin',
      isTicketingMemberRole: roleName === 'ticketing-member',
      isExternalUserRole: roleName === 'external-user',
    };
  }, [user?.role?.name]);

  const {
    isAdminRole,
    isAgentRole,
    isSupervisorRole,
    isTicketingAdminRole,
    isTicketingMemberRole,
    isExternalUserRole,
  } = roleChecks;

  const isNolimitCareRole = useMemo(
    () => isAdminRole || isAgentRole || isSupervisorRole,
    [isAdminRole, isAgentRole, isSupervisorRole]
  );

  const isTicketingRole = useMemo(
    () => isTicketingAdminRole || isTicketingMemberRole,
    [isTicketingAdminRole, isTicketingMemberRole]
  );

  const isExternalUser = useMemo(() => isExternalUserRole, [isExternalUserRole]);

  // Helper function to fetch user data
  const fetchUserData = useCallback(async (authToken: string): Promise<IUser> => {
    const response = await axios.get<ApiResponse<IUser>>(USER_ME_ENDPOINT, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data.payload;
  }, []);

  // Helper function to handle successful login
  const handleSuccessfulLogin = useCallback(
    async (authToken: string) => {
      try {
        const userData = await fetchUserData(authToken);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: userData, token: authToken } });
        setUser(userData);
        authLocalStorage.setToken(authToken);
        authLocalStorage.setUser(userData);
        navigate(redirectTo ?? DEFAULT_REDIRECT_PATH);
      } catch (error) {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom);
        dispatch({ type: 'LOGIN_ERROR', payload: errorMessage });
        showToast({ type: 'error', message: 'Failed to fetch user data' });
      }
    },
    [fetchUserData, setUser, navigate, showToast, redirectTo]
  );

  const mutationLogin = useLogin({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom);
        dispatch({ type: 'LOGIN_ERROR', payload: errorMessage });
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: (data) => {
        handleSuccessfulLogin(data.payload.token);
      },
    },
  });

  // EFFECTS
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const storedUser = authLocalStorage.getUser();
        const storedToken = authLocalStorage.getToken();

        if (storedUser && storedToken) {
          // Validate token by fetching fresh user data
          try {
            const freshUserData = await fetchUserData(storedToken);

            // Only update state if component is still mounted (prevents memory leaks)
            if (isMounted) {
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user: freshUserData, token: storedToken } });
              setUser(freshUserData);
              authLocalStorage.setUser(freshUserData);
            }
          } catch (_error) {
            // Token is invalid, clear storage (only if still mounted)
            if (isMounted) {
              authLocalStorage.clearUser();
              authLocalStorage.clearToken();
            }
          }
        } else {
          // No stored data, ensure clean state (only if still mounted)
          if (isMounted) {
            authLocalStorage.clearUser();
            authLocalStorage.clearToken();
          }
        }
      } catch (_error) {
        // Handle any localStorage errors (only if still mounted)
        if (isMounted) {
          authLocalStorage.clearUser();
          authLocalStorage.clearToken();
        }
      } finally {
        // Only set hydrated if component is still mounted
        if (isMounted) {
          setHydrated(true);
        }
      }
    };

    initializeAuth();

    // Cleanup function to prevent memory leaks from state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [fetchUserData, setUser]);

  // EVENT HANDLERS
  const handleLogin = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (state.loading) return;

      if (!email?.trim() || !password?.trim()) {
        const errorMessage = 'Email and password are required';
        dispatch({ type: 'LOGIN_ERROR', payload: errorMessage });
        showToast({ type: 'error', message: errorMessage });
        return;
      }

      mutationLogin.mutate({ email: email.trim(), password });
    },
    [state.loading, mutationLogin, showToast]
  );

  const handleLogout = useCallback(() => {
    try {
      // Clear localStorage
      deleteLocalStorageData(TOKEN_KEY);
      deleteLocalStorageData(USER_KEY);
      deleteLocalStorageData('ticketing-notifications');
      // deleteLocalStorageData('enabledSoundOne');
      // deleteLocalStorageData('enabledSoundTwo');
      // deleteLocalStorageData('enabledSoundThree');

      // Reset stores with reset methods
      analyticStore.getState().actions.setClearFilter();
      caseNotificationStore.getState().actions.clearCaseNotifications();
      // useAnalyticStore.getState().actions.setClearFilters();

      // Reset new case store
      const caseActions = casesStore.getState().actions;
      caseActions.setListActiveCase([]);
      caseActions.setListUnassignCase([]);
      caseActions.setListResolvedCase([]);
      caseActions.setResetSelectedCases();
      caseActions.setResetSelectedCase();
      caseActions.setResetSelectedMenuCaseMode();
      casesStore.setState({
        filterResolvedCase: defaultFilterCase,
        pendingFilterResolvedCase: defaultFilterCase,
        filterActiveCase: defaultFilterCase,
        pendingFilterActiveCase: defaultFilterCase,
        filterUnassignCase: defaultFilterCase,
        pendingFilterUnassignCase: defaultFilterCase,
      });

      // Reset app store
      useStore.setState({
        lastChatIndex: 0,
        messagesByCase: {},
        selectedCaseId: null,
        onlineUsers: [],
        pendingMessageIds: [],
        filterChatList: {
          labels: [],
          channels: [],
          agents: [],
          teams: [],
          searchCustomer: '',
        },
      });

      // Reset layout store
      layoutStore.setState({
        isOpenSidebar: false,
        isOpenDrawer: true,
        isHasDrawer: false,
      });

      // Reset file preview store
      filePreviewStore.getState().actions.setResetDefaultFileValues();
      filePreviewStore.setState({ isOpenFilePreviewModal: false });

      // Reset auth state
      dispatch({ type: 'LOGOUT' });
      setUser({} as IUser);

      queryClient.clear();

      navigate('/login');
      showToast({ type: 'success', message: 'Logout successful' });
    } catch (_error) {
      // Ensure logout state is set even if localStorage fails
      dispatch({ type: 'LOGOUT' });
    }
  }, [setUser, showToast, navigate, queryClient]);

  const handleLoginWithToken = useCallback(
    async (providedToken: string): Promise<IUser> => {
      try {
        const userData = await fetchUserData(providedToken);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: userData, token: providedToken } });
        setUser(userData);
        authLocalStorage.setToken(providedToken);
        authLocalStorage.setUser(userData);
        return userData;
      } catch (error) {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom);
        dispatch({ type: 'LOGIN_ERROR', payload: errorMessage });
        throw error;
      }
    },
    [fetchUserData, setUser]
  );

  /**
   * Update user helper
   * - Keeps auth reducer state in sync
   * - Updates zustand user store
   * - Persists to localStorage
   */
  const handleUpdateUser = useCallback(
    (updatedUser: IUser) => {
      // Reuse LOGIN_SUCCESS to update only the user while keeping the current token (if available)
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user: updatedUser, token: token ?? '' } });
      authLocalStorage.setUser(updatedUser);
    },
    [token]
  );

  // CONTEXT VALUE
  const authContext: AuthContextType = useMemo(
    () => ({
      user,
      isExternalUserRole,
      isExternalUser,
      isAdminRole,
      isAgentRole,
      isSupervisorRole,
      isTicketingAdminRole,
      isTicketingMemberRole,
      isNolimitCareRole,
      isTicketingRole,
      state,
      handleLogin,
      isLoadingLogin: mutationLogin.isPending,
      handleLoginWithToken,
      handleLogout,
      isAuthenticated,
      hydrated,
      token,
      handleUpdateUser,
    }),
    [
      user,
      isExternalUser,
      isAdminRole,
      isAgentRole,
      isSupervisorRole,
      isTicketingAdminRole,
      isTicketingMemberRole,
      isNolimitCareRole,
      isTicketingRole,
      state,
      handleLogin,
      handleLoginWithToken,
      handleLogout,
      isAuthenticated,
      hydrated,
      token,
      handleUpdateUser,
    ]
  );

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthContextProvider');
  return ctx;
};
export default useAuthContext;
